import { REPORT_SENTIMENT_ENABLED } from '../../../lib/report-entitlements';
import type { ReportAiContext } from '../../../functions/api/gemini/content-pipeline/types';
/**
 * Print Report Utility
 *
 * Opens a browser print dialog with a clean, self-contained HTML document
 * styled for light-theme printing (white background, dark text).
 * Includes interview context, holito logo, and inline sentiment chart.
 * Closes the tab automatically when the print dialog is dismissed.
 */

interface SentimentDataPoint {
  turnIndex: number;
  score: number;
  label: 'positive' | 'negative' | 'neutral';
  snippet: string;
  timestamp: number;
}

interface KeyMoment {
  turnIndex: number;
  label: string;
  score: number;
  timestamp: number;
}

interface ReportQuote {
  text: string;
  context: string;
  turnIndex: number;
  timestamp: number;
  sentiment?: 'positive' | 'negative' | 'neutral';
}

interface ReportFinding {
  insight: string;
  category: 'insight' | 'pain_point' | 'opportunity' | 'data_point';
}

interface ThemeAnalysis {
  theme: string;
  percentage: number;
  avgSentiment: number;
  sentimentLabel: 'positive' | 'negative' | 'neutral';
  turnIndices: number[];
}

interface StructuredReportData {
  /** Public web context, distinct from transcript findings. */
  aiContext?: ReportAiContext[];
  /** False for server-redacted basic report payloads. */
  advancedAnalyticsAvailable?: boolean;
  tldr: string;
  keyFindings: ReportFinding[];
  notableQuotes: ReportQuote[];
  recommendedActions: string[];
  openQuestions: string[];
  sentimentAnalysis: SentimentDataPoint[];
  keyMoments: KeyMoment[];
  thematicAnalysis: ThemeAnalysis[];
}

export interface InterviewContext {
  targetName: string;
  goal: string;
  details: string;
  intervieweeName?: string;
  intervieweeJob?: string;
  intervieweeCompany?: string;
}

export interface CapturedCharts {
  sentimentJourneySvg?: string;
  sentimentOverviewSvg?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function sentimentColor(sentiment: 'positive' | 'negative' | 'neutral'): string {
  switch (sentiment) {
    case 'positive': return '#059669';
    case 'negative': return '#DC2626';
    case 'neutral':
    default: return '#D97706';
  }
}

function categoryLabel(cat: ReportFinding['category']): string {
  switch (cat) {
    case 'insight': return 'Insight';
    case 'pain_point': return 'Pain Point';
    case 'opportunity': return 'Opportunity';
    case 'data_point':
    default: return 'Data Point';
  }
}

function categoryColor(cat: ReportFinding['category']): string {
  switch (cat) {
    case 'insight': return '#6366F1';
    case 'pain_point': return '#DC2626';
    case 'opportunity': return '#059669';
    case 'data_point':
    default: return '#2563EB';
  }
}

// ---------------------------------------------------------------------------
// Sentiment summary stats
// ---------------------------------------------------------------------------

function computeSentimentStats(data: SentimentDataPoint[]) {
  const total = data.length || 1;
  const positive = data.filter(d => d.label === 'positive').length;
  const neutral = data.filter(d => d.label === 'neutral').length;
  const negative = data.filter(d => d.label === 'negative').length;
  const avg = data.reduce((sum, d) => sum + d.score, 0) / total;
  return {
    positive: Math.round((positive / total) * 100),
    neutral: Math.round((neutral / total) * 100),
    negative: Math.round((negative / total) * 100),
    avgScore: avg,
  };
}

// ---------------------------------------------------------------------------
// Inline SVG sentiment journey chart
// ---------------------------------------------------------------------------

function buildSentimentSvg(data: SentimentDataPoint[], keyMoments: KeyMoment[]): string {
  if (data.length < 2) return '';

  const W = 700;
  const H = 200;
  const PAD_X = 40;
  const PAD_Y = 20;
  const chartW = W - PAD_X * 2;
  const chartH = H - PAD_Y * 2;

  const xScale = (i: number) => PAD_X + (i / (data.length - 1)) * chartW;
  const yScale = (score: number) => PAD_Y + ((1 - score) / 2) * chartH;

  // Build the line path
  const linePts = data.map((d, i) => `${xScale(i).toFixed(1)},${yScale(d.score).toFixed(1)}`);
  const lineD = `M${linePts.join(' L')}`;

  // Build the gradient fill area
  const areaD = `${lineD} L${xScale(data.length - 1).toFixed(1)},${yScale(0).toFixed(1)} L${xScale(0).toFixed(1)},${yScale(0).toFixed(1)} Z`;

  // Key moment dots
  const momentDots = keyMoments.map(km => {
    const pt = data.find(d => d.turnIndex === km.turnIndex);
    if (!pt) return '';
    const idx = data.indexOf(pt);
    const cx = xScale(idx).toFixed(1);
    const cy = yScale(pt.score).toFixed(1);
    return `<circle cx="${cx}" cy="${cy}" r="4" fill="${sentimentColor(pt.label)}" stroke="#fff" stroke-width="1.5"/>`;
  }).join('');

  return `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="100%" style="max-width:${W}px;display:block;margin:0 auto;">
    <defs>
      <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#059669" stop-opacity="0.3"/>
        <stop offset="50%" stop-color="#D97706" stop-opacity="0.05"/>
        <stop offset="100%" stop-color="#DC2626" stop-opacity="0.3"/>
      </linearGradient>
    </defs>
    <!-- zero line -->
    <line x1="${PAD_X}" y1="${yScale(0).toFixed(1)}" x2="${W - PAD_X}" y2="${yScale(0).toFixed(1)}" stroke="#e5e7eb" stroke-dasharray="4"/>
    <!-- labels -->
    <text x="${PAD_X - 6}" y="${PAD_Y + 4}" font-size="10" fill="#9ca3af" text-anchor="end">+1</text>
    <text x="${PAD_X - 6}" y="${yScale(0)}" font-size="10" fill="#9ca3af" text-anchor="end">0</text>
    <text x="${PAD_X - 6}" y="${H - PAD_Y}" font-size="10" fill="#9ca3af" text-anchor="end">-1</text>
    <!-- area fill -->
    <path d="${areaD}" fill="url(#sg)" opacity="0.5"/>
    <!-- line -->
    <path d="${lineD}" fill="none" stroke="#6366F1" stroke-width="2" stroke-linejoin="round"/>
    <!-- key moments -->
    ${momentDots}
  </svg>`;
}

// ---------------------------------------------------------------------------
// Inline SVG donut chart for sentiment distribution
// ---------------------------------------------------------------------------

function buildDonutSvg(stats: { positive: number; neutral: number; negative: number }): string {
  const size = 120;
  const cx = size / 2;
  const cy = size / 2;
  const r = 44;
  const stroke = 12;

  const segments = [
    { pct: stats.positive, color: '#059669' },
    { pct: stats.neutral, color: '#D97706' },
    { pct: stats.negative, color: '#DC2626' },
  ].filter(s => s.pct > 0);

  const circumference = 2 * Math.PI * r;
  let offset = 0;
  const arcs = segments.map(s => {
    const dashLen = (s.pct / 100) * circumference;
    const dashGap = circumference - dashLen;
    const arc = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${s.color}" stroke-width="${stroke}" stroke-dasharray="${dashLen.toFixed(1)} ${dashGap.toFixed(1)}" stroke-dashoffset="${(-offset).toFixed(1)}" transform="rotate(-90 ${cx} ${cy})"/>`;
    offset += dashLen;
    return arc;
  }).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">${arcs}</svg>`;
}

// ---------------------------------------------------------------------------
// holito logo SVG (inline, black fill for print)
// ---------------------------------------------------------------------------

const HOLITO_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 556 172.53" width="120" style="display:block;"><path fill="#0f172b" d="m78.62 169.72 15.16-69.7c.47-1.94 1.2-5.54 1.2-7.21 0-10.82-8.65-14.89-19-14.89-11.28 0-25.14 6.54-27.94 15.62-5.41 17.63-17.49 76.18-17.49 76.18H0L35.32 9.38h30.55L54.49 64.06c12.69-7.01 20.5-10.48 37.56-10.48 22.37 0 35.82 8.88 35.82 26.91 0 2.17-.73 6.98-1.2 9.38l-17.56 79.82H78.56l.07.03ZM195.76 53.61c32.38 0 50.48 21.53 50.48 50.38 0 34.35-24.4 65.73-58.89 65.73-32.38 0-50.28-21.3-50.28-50.14 0-34.59 24.4-65.97 58.69-65.97m-.87 25.88c-18.09 0-30.28 18.76-30.28 38.46 0 16.02 8.85 25.87 23.77 25.87 17.89 0 30.08-18.56 30.08-38.46 0-16.02-8.85-25.87-23.57-25.87M246.97 169.72 282.32 9.38h30.55l-35.32 160.34zM301.12 169.72l25.71-116.11h30.55l-25.71 116.11zM351.59 0c9.61 0 16.36 7.21 16.36 15.62 0 13.45-11.05 20.9-20.43 20.9s-16.36-6.98-16.36-15.62c0-13.22 11.05-20.9 20.43-20.9M371.56 139.41l12.99-59.39h-19.23l6.01-26.44h8.05c6.54 0 12.18-4.54 13.59-10.92l4.57-20.8h30.78l-6.98 31.71h23.57l-6.24 26.44H415.1l-11.05 50.48c-.23 1.44-.47 3.37-.47 4.81 0 6.24 3.61 10.08 11.28 10.08 3.14 0 6.98-1.2 8.41-2.4l1.2 24.5c-4.57 3.14-11.78 5.04-19.23 5.04-20.66 0-34.62-7.95-34.62-25.24 0-1.67.23-5.04.97-7.95l-.03.07ZM505.52 53.61c32.38 0 50.48 21.53 50.48 50.38 0 34.35-24.4 65.73-58.89 65.73-32.38 0-50.28-21.3-50.28-50.14 0-34.59 24.4-65.97 58.69-65.97m-.83 25.88c-18.09 0-30.28 18.76-30.28 38.46 0 16.02 8.85 25.87 23.77 25.87 17.89 0 30.08-18.56 30.08-38.46 0-16.02-8.85-25.87-23.57-25.87"/></svg>`;

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function printReport(data: StructuredReportData, context?: InterviewContext, charts?: CapturedCharts): void {
  const stats = computeSentimentStats(REPORT_SENTIMENT_ENABLED ? data.sentimentAnalysis : []);
  // Prefer captured DOM charts; fall back to hand-built SVGs
  const sentimentSvg = !REPORT_SENTIMENT_ENABLED || data.advancedAnalyticsAvailable === false ? '' : charts?.sentimentJourneySvg || buildSentimentSvg(data.sentimentAnalysis, data.keyMoments);
  const donutSvg = REPORT_SENTIMENT_ENABLED ? charts?.sentimentOverviewSvg || buildDonutSvg(stats) : '';

  // Build sentiment data table (replaces hover tooltips)
  const sortedSentiment = REPORT_SENTIMENT_ENABLED ? [...data.sentimentAnalysis].sort((a, b) => a.turnIndex - b.turnIndex) : [];
  const sentimentTableRows = sortedSentiment.map(d => {
    const c = sentimentColor(d.label);
    return `<tr>
      <td class="ts">${d.timestamp != null ? formatTimestamp(d.timestamp) : `Turn ${d.turnIndex}`}</td>
      <td class="score" style="color:${c}">${d.score > 0 ? '+' : ''}${d.score.toFixed(2)}</td>
      <td><span class="sentiment-pill" style="background:${c}15;color:${c}">${d.label}</span></td>
      <td class="snippet">&ldquo;${escapeHtml(d.snippet)}&rdquo;</td>
    </tr>`;
  }).join('');

  // Interview context section
  const contextHtml = context ? `
  <div class="section context-section">
    <h2>Interview Context</h2>
    <div class="context-grid">
      <div>
        <div class="context-label">Subject</div>
        <div class="context-value">${escapeHtml(context.targetName)}</div>
      </div>
      <div>
        <div class="context-label">Goal</div>
        <div class="context-value">${escapeHtml(context.goal)}</div>
      </div>
      ${context.details ? `
      <div>
        <div class="context-label">Key Details</div>
        <div class="context-tags">
          ${context.details.split(';').map(d => d.trim()).filter(d => d).map(d =>
            `<span class="tag">${escapeHtml(d)}</span>`
          ).join('')}
        </div>
      </div>` : ''}
      ${context.intervieweeName ? `
      <div>
        <div class="context-label">Interviewee</div>
        <div class="context-value">${escapeHtml(context.intervieweeName)}${context.intervieweeJob ? ` &mdash; ${escapeHtml(context.intervieweeJob)}` : ''}${context.intervieweeCompany ? `, ${escapeHtml(context.intervieweeCompany)}` : ''}</div>
      </div>` : ''}
    </div>
  </div>` : '';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Interview Report</title>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #1a1a1a;
    background: #fff;
    padding: 40px 48px;
    line-height: 1.6;
    font-size: 14px;
  }
  .header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 32px;
    padding-bottom: 20px;
    border-bottom: 2px solid #e5e7eb;
  }
  .header-left h1 { font-size: 24px; font-weight: 700; margin-bottom: 4px; }
  .header-left .date { color: #6b7280; font-size: 13px; }
  h2 {
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #6b7280;
    margin-bottom: 12px;
    padding-bottom: 6px;
    border-bottom: 1px solid #e5e7eb;
  }
  .section { margin-bottom: 32px; }
  .context-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .context-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; margin-bottom: 2px; }
  .context-value { font-size: 14px; color: #1a1a1a; }
  .context-tags { display: flex; flex-wrap: wrap; gap: 6px; }
  .tag { display: inline-block; padding: 3px 10px; background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 12px; }
  .tldr {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px 20px;
    font-size: 15px;
    line-height: 1.7;
    color: #374151;
  }
  .chart-section {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 16px;
  }
  .chart-section h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; margin-bottom: 12px; }
  .sentiment-stats-row {
    display: flex;
    gap: 16px;
    align-items: center;
    margin-top: 16px;
  }
  .sentiment-stats-row .donut { flex-shrink: 0; }
  .sentiment-stats-grid { display: flex; gap: 16px; flex-wrap: wrap; flex: 1; }
  .stat-box {
    flex: 1;
    min-width: 100px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px 14px;
    text-align: center;
  }
  .stat-box .value { font-size: 18px; font-weight: 700; }
  .stat-box .label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 2px; }
  .themes-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
  }
  .theme-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px 16px;
  }
  .theme-card .name { font-weight: 600; margin-bottom: 4px; }
  .theme-card .meta { font-size: 12px; color: #6b7280; }
  .bar-track { height: 6px; background: #e5e7eb; border-radius: 3px; margin-top: 6px; }
  .bar-fill { height: 6px; border-radius: 3px; }
  .quote-card {
    padding: 16px 20px;
    margin-bottom: 12px;
    border-radius: 8px;
  }
  .quote-card .text { font-style: italic; font-size: 14px; margin-bottom: 4px; }
  .quote-card .context { font-size: 12px; color: #6b7280; }
  .finding-row { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 8px; }
  .finding-dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 6px; flex-shrink: 0; }
  .finding-text { flex: 1; }
  .finding-cat { font-size: 11px; color: #6b7280; margin-top: 2px; }
  ul.actions { list-style: disc; padding-left: 20px; }
  ul.actions li { margin-bottom: 6px; }
  ul.questions { list-style: none; padding-left: 0; }
  ul.questions li { margin-bottom: 6px; padding-left: 18px; position: relative; }
  ul.questions li::before { content: "?"; position: absolute; left: 0; color: #6b7280; font-weight: 700; font-family: monospace; }
  .sentiment-table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 12px; }
  .sentiment-table th { text-align: left; padding: 6px 10px; border-bottom: 2px solid #e5e7eb; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; font-size: 10px; }
  .sentiment-table td { padding: 6px 10px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
  .sentiment-table .score { font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .sentiment-table .snippet { color: #374151; font-style: italic; }
  .sentiment-table .ts { color: #9ca3af; font-variant-numeric: tabular-nums; }
  .sentiment-pill { display: inline-block; padding: 1px 6px; border-radius: 4px; font-size: 10px; font-weight: 600; }
  .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #9ca3af; }
  @media print {
    body { padding: 20px; }
    .section { break-inside: avoid; }
  }
</style>
</head>
<body>

<div class="header">
  <div class="header-left">
    <h1>Interview Report</h1>
    <div class="date">Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
  </div>
  ${HOLITO_LOGO_SVG}
</div>

${contextHtml}

<div class="section">
  <h2>TL;DR</h2>
  <div class="tldr">${escapeHtml(data.tldr)}</div>
</div>

${sentimentSvg ? `
<div class="section">
  <h2>Sentiment Journey</h2>
  <div class="chart-section">
    ${sentimentSvg}
  </div>
  <div class="sentiment-stats-row">
    <div class="donut">${donutSvg}</div>
    <div class="sentiment-stats-grid">
      <div class="stat-box">
        <div class="value" style="color:${stats.avgScore >= 0 ? '#059669' : '#DC2626'}">${stats.avgScore >= 0 ? '+' : ''}${stats.avgScore.toFixed(2)}</div>
        <div class="label">Average</div>
      </div>
      <div class="stat-box">
        <div class="value" style="color:#059669">${stats.positive}%</div>
        <div class="label">Positive</div>
      </div>
      <div class="stat-box">
        <div class="value" style="color:#D97706">${stats.neutral}%</div>
        <div class="label">Neutral</div>
      </div>
      <div class="stat-box">
        <div class="value" style="color:#DC2626">${stats.negative}%</div>
        <div class="label">Negative</div>
      </div>
    </div>
  </div>
  <table class="sentiment-table">
    <thead>
      <tr>
        <th>Time</th>
        <th>Score</th>
        <th>Sentiment</th>
        <th>Snippet</th>
      </tr>
    </thead>
    <tbody>
      ${sentimentTableRows}
    </tbody>
  </table>
</div>` : ''}

${data.advancedAnalyticsAvailable !== false && data.thematicAnalysis.length > 0 ? `
<div class="section">
  <h2>Themes</h2>
  <div class="themes-grid">
    ${data.thematicAnalysis.map(t => `
      <div class="theme-card">
        <div class="name">${escapeHtml(t.theme)}</div>
        <div class="meta">${t.percentage}% of conversation</div>
        <div class="bar-track"><div class="bar-fill" style="width:${t.percentage}%;background:${REPORT_SENTIMENT_ENABLED ? sentimentColor(t.sentimentLabel) : '#64748b'}"></div></div>
      </div>
    `).join('')}
  </div>
</div>

` : ''}

${data.notableQuotes.length > 0 ? `<div class="section">
  <h2>Key Quotes</h2>
  ${data.notableQuotes.map(q => {
    const c = REPORT_SENTIMENT_ENABLED && q.sentiment ? sentimentColor(q.sentiment) : '#64748b';
    return `
    <div class="quote-card" style="background:${c}08;border:1px solid ${c}30;">
      <div class="text">&ldquo;${escapeHtml(q.text)}&rdquo;</div>
      <div class="context">${escapeHtml(q.context)}</div>
    </div>`;
  }).join('')}
</div>` : ''}

${data.keyFindings.length > 0 ? `<div class="section">
  <h2>Key Insights</h2>
  ${data.keyFindings.map(f => `
    <div class="finding-row">
      <div class="finding-dot" style="background:${categoryColor(f.category)}"></div>
      <div class="finding-text">
        ${escapeHtml(f.insight)}
        <div class="finding-cat">${categoryLabel(f.category)}</div>
      </div>
    </div>
  `).join('')}
</div>` : ''}

${data.recommendedActions.length > 0 ? `<div class="section">
  <h2>Recommended Actions</h2>
  <ul class="actions">
    ${data.recommendedActions.map(a => `<li>${escapeHtml(a)}</li>`).join('')}
  </ul>
</div>` : ''}

${data.openQuestions.length > 0 ? `<div class="section">
  <h2>Open Questions</h2>
  <ul class="questions">
    ${data.openQuestions.map(q => `<li>${escapeHtml(q)}</li>`).join('')}
  </ul>
</div>` : ''}

<div class="footer">holito.io</div>

</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.addEventListener('load', () => {
    printWindow.print();
    // Close the tab after the print dialog is dismissed
    printWindow.addEventListener('afterprint', () => {
      printWindow.close();
    });
    // Fallback: also close on focus return (some browsers don't fire afterprint)
    const fallback = setTimeout(() => {
      try { printWindow.close(); } catch { /* already closed */ }
    }, 1000);
    printWindow.addEventListener('afterprint', () => clearTimeout(fallback));
  });
}
